import React, { useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import { useAxiosInstance } from "../../services/Interceptor";
import LoadingSpinner from "../LoadingSpinner";
import { getAthleteCombineHistory } from "../../services/AthleteService";

const MEASUREMENT_ROWS = [
  "Weight",
  "Height",
  "Wingspan",
  "Hand",
  "Foot",
  "40 Yard Dash",
  "10 Yard Split",
  "20 Yard Split",
  "Bench Press",
  "Broad Jump",
  "Vertical Jump",
  "20-Yard Shuttle",
  "3-Cone L-Drill",
];

const CARD_HEADER_HEIGHT = 64; // h-[60px] card header + mb-1 (4px)

function ProgressionArrow({ status }) {
  if (status === "Better")
    return <span className="ml-2 text-green-500 font-normal">↑</span>;
  if (status === "Worse")
    return <span className="ml-2 text-red-500 font-normal">↓</span>;
  return null;
}

export default function AthleteCombineHistoryModal({
  open,
  onClose,
  athleteId,
  currentAthlete,
}) {
  const apiInstance = useAxiosInstance();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hoveredRow, setHoveredRow] = useState(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current && events.length > 0) {
      scrollRef.current.scrollLeft = scrollRef.current.scrollWidth;
    }
  }, [events]);

  useEffect(() => {
    if (!open || !athleteId) return;

    async function fetchHistory() {
      setLoading(true);
      setEvents([]);
      try {
        const data = await getAthleteCombineHistory(apiInstance, athleteId);
        setEvents(data ?? []);
      } catch (error) {
        toast.error(error?.message || "Failed to load combine history", {
          position: "top-right",
        });
      } finally {
        setLoading(false);
      }
    }

    fetchHistory();
  }, [open, athleteId, apiInstance]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white w-[95%] sm:w-[90%] md:w-full max-w-4xl rounded-md relative max-h-[83vh] flex flex-col">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-800 z-50"
        >
          <FontAwesomeIcon icon={faXmark} className="h-5 w-5" />
        </button>

        {/* Header */}
        <div className="px-6 pt-5 pb-2">
          <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-3">
            Compare Myself
          </h2>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl overflow-hidden bg-gray-200 shrink-0">
              <img
                src={
                  currentAthlete?.profilePicture || "/default-profile-img.png"
                }
                alt={currentAthlete?.firstName ?? ""}
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <p className="font-semibold text-blue-600 text-base">
                {currentAthlete
                  ? `${currentAthlete.firstName} ${currentAthlete.lastName}`
                  : "—"}
              </p>
              <p className="text-gray-500 text-xs">
                {currentAthlete?.primaryPosition ?? ""}
              </p>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto h-full pb-4">
          {loading ? (
            <div className="flex items-center justify-center h-48">
              <LoadingSpinner />
            </div>
          ) : events.length === 0 ? (
            <div className="flex items-center justify-center h-48 text-gray-400 text-sm">
              No combine history found.
            </div>
          ) : (
            <div className="flex overflow-y-auto h-full">
              {/* Fixed label column */}
              <div
                className={`shrink-0 sticky left-0 bg-white z-10 pl-6 pr-0 transition-shadow duration-200 ${isScrolled ? "shadow-md" : ""}`}
              >
                <div style={{ height: CARD_HEADER_HEIGHT }} />
                <ul>
                  {MEASUREMENT_ROWS.map((rowName) => (
                    <li
                      key={rowName}
                      onMouseEnter={() => setHoveredRow(rowName)}
                      onMouseLeave={() => setHoveredRow(null)}
                      className={`h-10 flex items-center text-sm pl-2 pr-8 font-medium text-gray-700 whitespace-nowrap border-b border-gray-200 last:border-0 ${hoveredRow === rowName ? "bg-blue-50" : ""}`}
                    >
                      {rowName}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Scrollable event cards */}
              <div
                ref={scrollRef}
                onScroll={(e) => setIsScrolled(e.currentTarget.scrollLeft > 0)}
                className="overflow-x-auto flex gap-3 pr-4 pb-4"
              >
                {events.map((event, colIdx) => (
                  <div key={event.id ?? colIdx} className="shrink-0 w-[150px]">
                    {/* Card header */}
                    <div className="h-[60px] flex flex-col items-center justify-center text-center mb-1">
                      <p
                        className="font-semibold text-sm text-gray-800 truncate w-full px-1"
                        title={event.eventName}
                      >
                        {event.eventName}
                      </p>
                      <p className="text-xs text-gray-600 mt-0.5">
                        {[event.city, event.state].filter(Boolean).join(", ")}
                      </p>
                      <p className="text-xs text-gray-600 mt-0.5">
                        {event.date ?? ""}
                      </p>
                    </div>

                    {/* Value list */}
                    <ul className="bg-gray-50 rounded-lg border border-gray-200">
                      {MEASUREMENT_ROWS.map((rowName) => {
                        const m = event.results?.find(
                          (x) => x.name === rowName,
                        );
                        return (
                          <li
                            key={rowName}
                            onMouseEnter={() => setHoveredRow(rowName)}
                            onMouseLeave={() => setHoveredRow(null)}
                            className={`h-10 flex items-center justify-center text-sm text-gray-700 px-3 border-b border-gray-200 last:border-0 ${hoveredRow === rowName ? "bg-blue-50" : ""}`}
                          >
                            {m?.value ?? "-"}
                            {colIdx > 0 && (
                              <ProgressionArrow status={m?.progressionStatus} />
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
